/**
 * Cookie-consent state, stored in a base-domain cookie so one choice covers every
 * tenant subdomain (a user is not re-prompted on `acme.recruit41.com` after choosing
 * on `recruit41.com`). The session and CSRF cookies are already base-domain scoped;
 * this matches them.
 *
 * The value carries a version. Bumping CONSENT_VERSION re-prompts everyone, which is
 * how a materially changed cookie policy re-collects consent.
 */

export type CookieConsent = 'granted' | 'denied';

export const CONSENT_VERSION = 'v1';

const COOKIE_NAME = 'r41_cookie_consent';
const MAX_AGE_SECONDS = 180 * 24 * 60 * 60;

/**
 * Base registrable domain for the current host, so the cookie is shared across
 * subdomains. `acme.recruit41.com` and `recruit41.com` both resolve to
 * `recruit41.com`; `learn41.com` is a separate base and keeps its own cookie.
 * Returns null for a single-label host (e.g. bare `localhost`), where a `Domain`
 * attribute is omitted and the cookie stays host-only.
 */
function baseDomain(host: string): string | null {
  const labels = host.split('.');
  if (labels.length < 2) return null;
  return labels.slice(-2).join('.');
}

function readRawCookie(name: string): string | null {
  const prefix = `${name}=`;
  const hit = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  return hit ? decodeURIComponent(hit.slice(prefix.length)) : null;
}

/**
 * The stored choice, or null when none has been made for the current version
 * (which is the signal to show the banner).
 */
export function getCookieConsent(): CookieConsent | null {
  try {
    const raw = readRawCookie(COOKIE_NAME);
    if (!raw) return null;
    const [version, status] = raw.split(':');
    if (version !== CONSENT_VERSION) return null;
    return status === 'granted' || status === 'denied' ? status : null;
  } catch {
    return null;
  }
}

export function setCookieConsent(value: CookieConsent): void {
  try {
    const base = baseDomain(window.location.hostname);
    const domain = base ? `; Domain=.${base}` : '';
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      `${COOKIE_NAME}=${encodeURIComponent(`${CONSENT_VERSION}:${value}`)}` +
      `; Path=/; Max-Age=${MAX_AGE_SECONDS}${domain}; SameSite=Lax${secure}`;
  } catch {
    // A page that cannot set the cookie keeps showing the banner, which is the safe
    // default: never silently treat an unstored choice as consent.
  }
}
